import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Box from '@repo/ui/box'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  MedicalConditionEnum,
  medicalConditionEnumMapping
} from '../../../lib/types/enums/enrollment/medical-condition-enum'
import { MedicalTermInput } from '../../../lib/types/models/medical-terms/medical-term-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import {
  createStringArraySchema,
  createStringSchema
} from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<MedicalTermInput> = z.object({
  label: createStringSchema('label'),
  level: createEnumSchema({ enumType: MedicalConditionEnum, fieldName: 'niveau' }),
  value: createStringArraySchema('valeur', { errorMessage: 'Veuillez saisir des valeurs' })
})

const MedicalTermForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<MedicalTermInput>) => {
  const form = useForm<MedicalTermInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form form={form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="flex flex-col gap-4"
      >
        <Box className="flex flex-col gap-4">
          <Typography variant="body-medium" color={tailwindColors.primary.DEFAULT}>
            Termes médicaux
          </Typography>
          <FormField
            control={form.control}
            name="label"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} required label="Label" type="text" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="level"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput<MedicalConditionEnum>
                    {...field}
                    required
                    options={Object.values(MedicalConditionEnum)
                      .filter(
                        (level) =>
                          ![MedicalConditionEnum.LEVEL_0, MedicalConditionEnum.RAS].includes(level)
                      )
                      .map((level) => ({
                        label: medicalConditionEnumMapping[level].label,
                        value: level
                      }))}
                    label={'Niveau'}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      medicalConditionEnumMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="value"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    required
                    type="text"
                    label={`Préciser la liste des mots qui déclenche la détection de ce terme médical (pex : "acarien, acariens, accarien, accariens,")`}
                    variant="primary"
                    onChange={field.onChange}
                    selectedOptions={
                      field.value?.map((value) => ({
                        label: value,
                        value: value
                      })) ?? []
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <ValidationButtons {...validationButtonProps} />
        </Box>
      </form>
    </Form>
  )
}

export default MedicalTermForm
